import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchStoresRequest,
  deleteStoreRequest,
} from "../../../../redux/StoreList/StoreSlice";
import { CiEdit } from "react-icons/ci";
import { RiDeleteBin6Line } from "react-icons/ri";
import TableCompo from "../../Table/TableCompo";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Box, CircularProgress, Button } from "@mui/material";
import useCsvConverter from "../../../../hooks/convertToCSV";
import { apiRequest } from "../../../../redux/Reports/reportsSlice";
import { canShowPermission } from "../../Permissions/permissionObject";

// Delete confirmation toast
const DeleteToast = ({ storeId, closeToast, dispatch }) => {
  const [yesLoading, setYesLoading] = useState(false);
  const [noLoading, setNoLoading] = useState(false);

  const handleYes = () => {
    setYesLoading(true);
    setTimeout(() => {
      dispatch(deleteStoreRequest(storeId));
      toast.success("Store deleted successfully!");
      closeToast();
    }, 1000);
  };

  const handleNo = () => {
    setNoLoading(true);
    setTimeout(() => {
      toast.error("Delete cancelled");
      closeToast();
    }, 500);
  };

  return (
    <div
      style={{
        background: "#fff",
        padding: "16px 20px",
        borderRadius: "8px",
        boxShadow: "0px 2px 10px rgba(0,0,0,0.4)",
        color: "#000",
        fontSize: "14px",
        maxWidth: "300px",
      }}
    >
      <p>Are you sure you want to change the status of store?</p>
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          gap: "10px",
          marginTop: "10px",
        }}
      >
        <button
          style={{
            background: "#8e5addff",
            color: "#fff",
            border: "none",
            padding: "6px 12px",
            borderRadius: "4px",
            cursor: "pointer",
            minWidth: "60px",
          }}
          disabled={yesLoading || noLoading}
          onClick={handleYes}
        >
          {yesLoading ? (
            <CircularProgress size={14} sx={{ color: "white" }} />
          ) : (
            "Yes"
          )}
        </button>
        <button
          style={{
            background: "#9e9e9e",
            color: "#fff",
            border: "none",
            padding: "6px 12px",
            borderRadius: "4px",
            cursor: "pointer",
            minWidth: "60px",
          }}
          disabled={yesLoading || noLoading}
          onClick={handleNo}
        >
          {noLoading ? (
            <CircularProgress size={14} sx={{ color: "white" }} />
          ) : (
            "No"
          )}
        </button>
      </div>
    </div>
  );
};

const CreateStoreTableList = ({ onEdit }) => {
  const dispatch = useDispatch();
  const {
    stores = [],
    loading,
    error,
    total = 0,
  } = useSelector((state) => state.store || {});
  const [pagination, setPagination] = useState({ page: 0, limit: 5 });
  const [searchText, setSearchText] = useState("");
  const [permissions, setPermissions] = useState({});

  useEffect(() => {
    const storedPermissions = localStorage.getItem("userPermissions");
    if (storedPermissions) {
      try {
        setPermissions(JSON.parse(storedPermissions));
      } catch (e) {
        console.error("Invalid permissions JSON", e);
      }
    }
  }, []);

  const canEdit = canShowPermission(permissions, "storemanagement", "edit");
  const canDelete = canShowPermission(permissions, "storemanagement", "delete");

  useEffect(() => {
    dispatch(
      fetchStoresRequest({
        page: pagination.page + 1,
        limit: pagination.limit,
        search: searchText,
      })
    );
  }, [dispatch, pagination, searchText]);

  const handleTableChange = (action, tableState) => {
    if (action === "changePage")
      setPagination((prev) => ({ ...prev, page: tableState.page }));
    else if (action === "changeRowsPerPage")
      setPagination({ page: 0, limit: tableState.rowsPerPage });
    else if (action === "search") {
      setSearchText(tableState.searchText || "");
      setPagination((prev) => ({ ...prev, page: 0 }));
    }
  };

  const handleDeleteClick = (storeId) => {
    toast(
      (t) => (
        <DeleteToast
          storeId={storeId}
          closeToast={() => toast.dismiss(t.id)}
          dispatch={dispatch}
        />
      ),
      {
        position: "top-center",
        autoClose: false,
        closeOnClick: false,
        closeButton: false,
        style: { padding: 0, background: "none", boxShadow: "none" },
      }
    );
  };

  const columns = [
    { field: "branchName", headerName: "Store Name" },
    { field: "branchCode", headerName: "Code" },
    { field: "phone", headerName: "Phone" },
    { field: "area", headerName: "Area" },
    { field: "city", headerName: "City" },
    {
      field: "email",
      headerName: "Email",
      minWidth: 250,
      render: (_, row) => (
        <Box
          sx={{ whiteSpace: "normal", wordBreak: "break-word", maxWidth: 250 }}
        >
          {row.email}
        </Box>
      ),
    },
    ...(canEdit
      ? [
          {
            field: "edit",
            headerName: "Edit",
            render: (_, row) => (
              <CiEdit
                style={{ cursor: "pointer" }}
                onClick={() => onEdit(stores.find((s) => s._id === row._id))}
              />
            ),
            hideInExcel: true,
          },
        ]
      : []),
    ...(canDelete
      ? [
          {
            field: "status",
            headerName: "Status",
            render: (_, row) => (
              <Button
                variant="contained"
                style={{
                  backgroundColor: row.status ? "green" : "#d32f2f",
                  color: "white",
                  textTransform: "none",
                }}
                onClick={() => handleDeleteClick(row._id)}
              >
                {row.status ? "Enable" : "Disable"}
              </Button>
            ),
            hideInExcel: true,
          },
        ]
      : []),
    {
      field: "createdBY",
      headerName: "Created By",
      width: 200,
      hideInExcel: true,
      render: (value, row) => {
        return row?.createdBy ? row.createdBy.name : "N/A";
      },
    },
    {
      field: "updatedBy",
      headerName: "Updated By",
      width: 200,
      hideInExcel: true,
      render: (value, row) => {
        return row?.updatedBy ? row.updatedBy.name : "N/A";
      },
    },
  ];

  const { convertToCsv } = useCsvConverter();

  const triggerCsvDownload = (csvText, filename) => {
    const blob = new Blob([csvText], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();

    window.URL.revokeObjectURL(url);
  };

  const handleDownloadStoreCsv = () => {
    console.log("Store CSV API called");

    dispatch(
      apiRequest({
        method: "GET",
        endpoint: "/api/v1/store/storesCsv",
        query: {},
        callback: (response) => {
          console.log("Store CSV response:", response);

          const csv = convertToCsv(response?.data || []);
          triggerCsvDownload(csv, "stores.csv");
        },
      })
    );
  };

  return (
    <div>
      {loading ? (
        <p>Loading...</p>
      ) : (
        <TableCompo
          columns={columns}
          data={Array.isArray(stores) ? [...stores].reverse() : []}
          loading={loading}
          totalCount={total}
          page={pagination.page}
          limit={pagination.limit}
          onTableChange={handleTableChange}
          extraButtons={
            <Button
              size="sm"
              variant="outlined"
              onClick={handleDownloadStoreCsv}
            >
              Download CSV
            </Button>
          }
        />
      )}
    </div>
  );
};

export default CreateStoreTableList;
